import React, { useEffect } from 'react';
import { X, Flag } from 'lucide-react';
import type { TaskActivityLog, CRMUser } from '../../types';
import UserAvatar from '../ui/UserAvatar';
import { isActionRequiredActivityField } from '../../utils/taskActivityLogger';

interface TaskActivityHistoryModalProps {
  open: boolean;
  onClose: () => void;
  taskId: string;
  activityLogs: TaskActivityLog[];
  teamMembers: CRMUser[];
}

function formatRelativeTime(d: Date) {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const TaskActivityHistoryModal: React.FC<TaskActivityHistoryModalProps> = ({
  open,
  onClose,
  taskId,
  activityLogs,
  teamMembers,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const forTask = activityLogs.filter((l) => l.taskId === taskId);
  const sorted = [...forTask].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const arLogs = sorted.filter((l) => isActionRequiredActivityField(l.field));
  const otherLogs = sorted.filter((l) => !isActionRequiredActivityField(l.field));

  const photoFor = (uid: string) => teamMembers.find((m) => m.uid === uid)?.photoURL;

  const renderEntry = (log: TaskActivityLog, emphasize: boolean) => (
    <div
      key={log.id || `${log.actorId}-${log.createdAt?.getTime?.()}-${log.action}-${log.field}`}
      className={`pm-activity-modal-entry ${emphasize ? 'pm-activity-modal-entry--ar' : ''}`}
    >
      <UserAvatar
        className="pm-avatar"
        name={log.actorName}
        photoURL={photoFor(log.actorId)}
        userId={log.actorId}
        style={{ width: 26, height: 26, fontSize: 10 }}
      />
      <div className="pm-activity-modal-entry-body">
        <div className="pm-activity-modal-entry-line">
          <strong>{log.actorName}</strong>
          <span className="pm-activity-modal-entry-action">{log.action}</span>
          {log.field && <span className="pm-activity-modal-entry-field">{log.field}</span>}
        </div>
        {(log.oldValue != null || log.newValue != null) && (
          <div className="pm-activity-modal-entry-values">
            {log.oldValue != null && (
              <span className="pm-activity-modal-old">{log.oldValue}</span>
            )}
            {log.oldValue != null && log.newValue != null && (
              <span className="pm-activity-modal-arrow" aria-hidden>
                →
              </span>
            )}
            {log.newValue != null && (
              <span className="pm-activity-modal-new">{log.newValue}</span>
            )}
          </div>
        )}
        <span className="pm-text-muted pm-activity-modal-time">{formatRelativeTime(log.createdAt)}</span>
      </div>
    </div>
  );

  return (
    <div className="pm-activity-modal-overlay" onClick={onClose} role="presentation">
      <div className="pm-activity-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="pm-activity-modal-title">
        <div className="pm-activity-modal-header">
          <h3 id="pm-activity-modal-title">Task activity</h3>
          <button type="button" className="pm-activity-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="pm-activity-modal-body pm-scroll">
          {sorted.length === 0 ? (
            <p className="pm-activity-modal-empty">No activity recorded yet.</p>
          ) : (
            <>
              {arLogs.length > 0 && (
                <section className="pm-activity-modal-section">
                  <h4 className="pm-activity-modal-section-title">
                    <Flag size={12} strokeWidth={2.5} aria-hidden />
                    Action required
                  </h4>
                  {arLogs.map((log) => renderEntry(log, true))}
                </section>
              )}
              {otherLogs.length > 0 && (
                <section className="pm-activity-modal-section">
                  <h4 className="pm-activity-modal-section-title">Other changes</h4>
                  {otherLogs.map((log) => renderEntry(log, false))}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskActivityHistoryModal;
